import React from 'react';
import {
  ChevronRightRounded as ChevronRightRoundedIcon,
  ExpandMoreRounded as ExpandMoreRoundedIcon,
} from '@mui/icons-material';
import { Box, Collapse, List, ListItemButton, Typography } from '@mui/material';
import { guaranteesLayoutStyles } from '../../styles.ts';
import {
  getCompactNavigationIcon,
  getNavigationItemPath,
  isNavigationItemActive,
  isNavigationItemGroup,
} from '../../utils.ts';
import type { IExpandedNavigationItemProps } from './types.ts';

interface IExpandedNavigationRowContentProps {
  isActive: boolean;
  isExpanded?: boolean;
  item: IExpandedNavigationItemProps['item'];
  showArrow?: boolean;
}

const ExpandedNavigationRowContent: React.FC<IExpandedNavigationRowContentProps> = ({
  isActive,
  isExpanded = false,
  item,
  showArrow = false,
}) => {
  const ItemIcon = getCompactNavigationIcon(item);

  return (
    <>
      <Box sx={guaranteesLayoutStyles.menuItemContent}>
        <Box sx={guaranteesLayoutStyles.menuItemLeading}>
          <Box sx={guaranteesLayoutStyles.menuItemIconWrap(isActive)}>
            <ItemIcon sx={guaranteesLayoutStyles.menuItemIcon(isActive)} />
          </Box>
          <Box sx={guaranteesLayoutStyles.menuItemLabelWrap}>
            <Typography sx={guaranteesLayoutStyles.menuItemText(isActive)}>{item.title}</Typography>
          </Box>
        </Box>
      </Box>
      {showArrow ? (
        isExpanded ? (
          <ExpandMoreRoundedIcon sx={guaranteesLayoutStyles.menuItemArrow} />
        ) : (
          <ChevronRightRoundedIcon sx={guaranteesLayoutStyles.menuItemArrow} />
        )
      ) : null}
    </>
  );
};

const ExpandedNavigationItem: React.FC<IExpandedNavigationItemProps> = ({
  currentPath,
  isExpanded,
  item,
  onNavigate,
  onToggle,
}) => {
  const isGroup = isNavigationItemGroup(item);
  const isActive = isNavigationItemActive(currentPath, item);
  const groupContent = isGroup ? (
    <>
      <ListItemButton onClick={onToggle} sx={guaranteesLayoutStyles.menuItemButton(isActive)}>
        <ExpandedNavigationRowContent
          isActive={isActive}
          isExpanded={isExpanded}
          item={item}
          showArrow
        />
      </ListItemButton>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List disablePadding sx={guaranteesLayoutStyles.menuNestedList}>
          {item.children.map((child) => {
            const isChildActive = isNavigationItemActive(currentPath, child, item.segment);

            return (
              <ListItemButton
                key={`${item.segment}-${child.segment}`}
                onClick={() => onNavigate(getNavigationItemPath(child, item.segment))}
                sx={guaranteesLayoutStyles.menuNestedItemButton(isChildActive)}
              >
                <Typography sx={guaranteesLayoutStyles.menuNestedItemText(isChildActive)}>
                  {child.title}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </>
  ) : null;
  const linkContent = (
    <ListItemButton
      onClick={() => onNavigate(getNavigationItemPath(item))}
      sx={guaranteesLayoutStyles.menuItemButton(isActive)}
    >
      <ExpandedNavigationRowContent isActive={isActive} item={item} />
    </ListItemButton>
  );

  return <Box>{isGroup ? groupContent : linkContent}</Box>;
};

export default ExpandedNavigationItem;
